/**
 * Story moderation (Backlog 060) and user referrals (Backlog 061 step 1), as the Admin API sends them
 * (AdminStoriesController, AdminUserReferralsController). Enums are numbers on the wire.
 */

/** FinaleAccessMode: 0 inherit the Story's access, 1 open to everyone, 2 its own access criteria. */
export type FinaleAccessModeValue = 0 | 1 | 2

export interface AdminStoryRowDto {
  id: string
  name: string
  tagline?: string | null
  areaLabel?: string | null
  isPublic: boolean
  isActive: boolean
  creatorId: string
  creatorDisplayName?: string | null
  /** Distinct stops across active chapters. */
  stopCount: number
  chapterCount: number
  hasFinale: boolean
  ratingAverage?: number | null
  /** Public review count (the Story's cache). */
  ratingCount: number
  /** Every review row, private notes included. */
  reviewCount: number
  featuredRank?: number | null
  /** The Story's own access criteria gate somebody. */
  hasAccessCriteria: boolean
  /** The Story's display criteria hide it from somebody. */
  hasDisplayCriteria: boolean
  finaleAccessMode: FinaleAccessModeValue
  /** Active chapters whose effective access is open to everyone. */
  openChapterCount: number
  /** Active chapters whose effective access gates somebody. */
  gatedChapterCount: number
  createdDate: string
  storyPublishedAt?: string | null
  /** StoryReviewState: 0 None, 1 Pending, 2 Approved, 3 Rejected. */
  storyReview: StoryReviewValue
  /** The moderator's reason on a rejection (shown to the Story's editors). */
  storyReviewNote?: string | null
  storyReviewedAt?: string | null
  storyReviewedById?: string | null
  storyReviewedByDisplayName?: string | null
  /** When it last went to Pending; the queue is oldest first. */
  storySubmittedAt?: string | null
  /** Players can find it: public, active and Approved. */
  isListed: boolean
}

/** StoryReviewState on the server (Backlog 060 moderation). */
export type StoryReviewValue = 0 | 1 | 2 | 3

export const STORY_REVIEW_LABELS: Record<number, string> = {
  0: 'Not submitted',
  1: 'Pending',
  2: 'Approved',
  3: 'Rejected',
}

export interface AdminStoryPageDto {
  items: AdminStoryRowDto[]
  /** Stories waiting for a moderator, whatever the filters (the queue badge). */
  pendingCount: number
  totalCount: number
  page: number
  pageSize: number
}

export type StoryVisibilityFilter = 'all' | 'public' | 'hidden'
/** `queue` = Pending first, oldest submission first. */
export type StorySort = 'queue' | 'featured' | 'name' | 'created' | 'rating'
export type StoryReviewFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'none'

export interface AdminStoryReviewDto {
  id: string
  userId: string
  userDisplayName?: string | null
  userEmail?: string | null
  stars?: number | null
  comment?: string | null
  containsSpoilers: boolean
  isPrivateNote: boolean
  /** Hidden by a moderator: out of every public list and the Story's rating. */
  isHidden: boolean
  hiddenAt?: string | null
  hiddenReason?: string | null
  reviewerProgress: { finished: boolean; completedChapters: number; totalChapters: number }
  createdAt: string
}

/** The Story reviews filter: every review, only those readers see, or only moderator-hidden ones. */
export type ReviewVisibilityFilter = 'all' | 'visible' | 'hidden'

export interface AdminStoryReviewPageDto {
  storyId: string
  storyName: string
  items: AdminStoryReviewDto[]
  totalCount: number
  page: number
  pageSize: number
}

export interface StoryAccessRuleDto {
  collectionId: string
  storyName: string
  storyIsPublic: boolean
  chapterId?: string | null
  chapterName?: string | null
  isFinale: boolean
  /** "access" decides who can start it; "display" decides who can see it. Every rule must pass. */
  category: 'access' | 'display'
  /** Where the rule is set: the Story itself, one chapter, or the finale. */
  source: 'story' | 'chapter' | 'finale'
  /** Plain words, e.g. "the explorer plan or higher OR owns the story", or "open". */
  summary: string
  updatedAt?: string | null
}

export interface AdventureStoryAccessDto {
  adventureId: string
  /** The stop's own condition gate, separate from any Story. */
  ownConditionGate: boolean
  /** Access and display rules; every one must pass. */
  rules: StoryAccessRuleDto[]
  /** Memberships whose access is open (category "access", summary "open"). */
  freeMemberships: StoryAccessRuleDto[]
}

export interface ReferralUserDto {
  id: string
  displayName?: string | null
  email?: string | null
  createdDate: string
  isActive: boolean
}

export interface AdminUserReferralsDto {
  userId: string
  referralCode?: string | null
  count: number
  threshold: number
  referredBy?: ReferralUserDto | null
  items: ReferralUserDto[]
  page: number
  pageSize: number
}

/** Content entitlements (who owns a Story or one chapter), AdminContentEntitlementsController. */
export type EntitlementSource = 'Purchase' | 'Grant' | 'Gift'

export interface ContentEntitlementDto {
  id: string
  userId: string
  collectionId: string
  storyName: string
  /** Null = the whole Story. */
  achievementId: string | null
  chapterName: string | null
  source: EntitlementSource
  contentPurchaseId: string | null
  grantedByUserId: string | null
  createdAt: string
  revokedAt: string | null
  revokeReason: string | null
  isLive: boolean
}

export interface GrantEntitlementRequest {
  collectionId: string
  /** Null = the whole Story. */
  achievementId: string | null
  reason?: string
}

export interface RevokeEntitlementRequest {
  reason?: string
}

export interface EntitlementStoryOptionDto {
  id: string
  name: string
  isPublic: boolean
  chapters: { id: string; name: string }[]
}
